import { Box, Button, Typography, useTheme } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CSSProperties, useCallback, useState } from 'react';
import { ObjectSchema } from 'yup';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { DefaultShareClassSchema, ViewModelInvestmentType } from '../../../schemas/common-schema-defs';
import {
  currentStepCapTableStateJ,
  shareClassesStateJ,
  sortedShareClassesStateJ,
} from '../../../services/state/CapTableFormStateJ';
import { FDMap } from '../../../util/data-structure/FDMap';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { useSchemaToFormFields } from '../../../util/schema-utils';
import { ShareClass, TypeToSchemaMap } from '../../../view-models/captable.view-model';
import { createForm } from '../../../view-models/form.view-model';
import { ConfigItemRow, IConfigItemRowProps } from '../../Config/Common/ConfigItemRow';
import { StepperFormButtons } from '../../Finance2/Forms/StepperFormButtons';
import { AddShareClassForm } from './AddShareClassForm';
import { useCancelButtonProps } from './CapTableForm';
import { FormCard } from './commonStyledComponents';
import { CaptableAsOfDate } from './SummaryGrid/CaptableAsOfDate';
import { useCaptableInvestmentCustomFields } from './useShareClassFields';

export function AddShareClass() {
  const [openModal, setOpenModal] = useState(false);
  const [shareClasses, setShareClasses] = useAtom(shareClassesStateJ);
  const sortedShareClasses = useAtomValue(sortedShareClassesStateJ);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const { colors } = useTheme();
  const cancelButtonProps = useCancelButtonProps();
  const { asOfCapTables } = useFlags<MaggieFeatureFlags>();

  const [selectedShareClass, setSelectedShareClass] = useState<ShareClass>(() =>
    DefaultShareClassSchema.create().cast({})
  );

  const onCreate = useCallback(() => {
    setFormMode('create');
    setSelectedShareClass(DefaultShareClassSchema.create().cast({}));
    setOpenModal(true);
  }, []);

  const onDelete = useCallback(
    (item: ShareClass) => {
      setShareClasses((currVal) => {
        return currVal.filter((currItem) => currItem.name !== item.name);
      });
    },
    [setShareClasses]
  );

  const onEdit = useCallback((item: ShareClass) => {
    setFormMode('edit');
    setSelectedShareClass({ ...item });
    setOpenModal(true);
  }, []);

  return (
    <>
      <FormCard>
        {asOfCapTables && (
          <Box mb='1rem'>
            <CaptableAsOfDate />
          </Box>
        )}
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Typography variant='subtitle1'>Please add investment information</Typography>
          <Button variant='contained' color='secondary' onClick={onCreate}>
            Add Investment
          </Button>
        </Box>
        {sortedShareClasses.length > 0 && (
          <Box style={{ border: `thin solid ${colors.neutral[20]}`, borderRadius: '4px', padding: '1rem' }}>
            <ShareClassList onEdit={onEdit} onDelete={onDelete} shareClasses={sortedShareClasses} />
          </Box>
        )}
        <BasicDialog
          open={openModal}
          onClose={() => {
            setOpenModal(false);
          }}
          title='Add Investment'
          disableRestoreFocus
        >
          {openModal ? (
            <AddShareClassForm
              onClose={() => setOpenModal(false)}
              shareClass={selectedShareClass as ShareClass}
              mode={formMode}
            />
          ) : (
            <></>
          )}
        </BasicDialog>
        <StepperFormButtons
          stepIsValid={() => Promise.resolve(shareClasses.length > 0)}
          stepState={currentStepCapTableStateJ}
          secondaryActionProps={cancelButtonProps}
        />
      </FormCard>
    </>
  );
}

type IShareClassListProps = {
  shareClasses: ShareClass[];
  style?: CSSProperties;
} & Pick<IConfigItemRowProps<ShareClass>, 'onEdit' | 'onDelete'>;

export function ShareClassList(props: IShareClassListProps) {
  const { onDelete, onEdit, shareClasses } = props;
  const schemaToFormField = useSchemaToFormFields();
  const customFields = useCaptableInvestmentCustomFields();

  const items = shareClasses.map((item) => {
    const schema = TypeToSchemaMap[item.type as ViewModelInvestmentType];
    const formFields = schemaToFormField(
      (schema.create() as ObjectSchema<ShareClass>).omit(['customData']) as ObjectSchema<ShareClass>
    );
    const form = createForm({
      data: item,
      fields: FDMap.fromArray(formFields.concat(customFields), 'key'),
    });

    return (
      <ConfigItemRow form={form} key={`${item.name}-${item.type}`} onDelete={onDelete} onEdit={onEdit} />
    );
  });

  return (
    <Box display={'grid'} gap={'1rem'} style={props.style}>
      {items}
    </Box>
  );
}
